import React, { useState } from 'react';
import { Box, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure, VStack, Text, Heading, FormControl, FormLabel, Input, Select, Textarea, Checkbox, HStack, SimpleGrid, Card, CardBody, useColorModeValue, Divider, useToast } from '@chakra-ui/react';
import HomeNavbar from '../components/HomeNavbar';
import { submitLead } from '../services/api';

const Home = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // State for form fields (optional, but good practice for controlled components)
  const [formData, setFormData] = useState({
    clubName: '',
    clubEmail: '',
    clubPhone: '',
    clubWebsite: '',
    clubCity: '',
    clubCountry: '',
    contactName: '',
    contactRole: '',
    contactEmail: '',
    contactPhone: '',
    services: [],
    otherService: '',
    clubNeeds: '',
    timeline: '',
    budget: '',
    authConfirm: false,
    termsAgree: false,
  });

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleMultiSelectChange = (e) => {
    const { id, options } = e.target;
    const value = [];
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        value.push(options[i].value);
      }
    }
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      await submitLead(formData);
      toast({
        title: 'Registration submitted!',
        description: 'Your club registration has been received.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error('Lead registration error:', error);
      toast({
        title: 'Submission failed',
        description: error.message || 'Could not submit registration.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <HomeNavbar />
      <VStack spacing={8} align="center" justify="center" height="calc(100vh - 64px)">
        <Heading as="h1" size="xl">Welcome to the Dashboard</Heading>
        <Text fontSize="lg" textAlign="center">
          Explore the features and manage your account.
        </Text>
        <Button colorScheme="teal" size="lg" onClick={onOpen}>
          Register Now
        </Button>
      </VStack>

      {/* Sections about the Dashboard */}
      <Box py={10} px={6} maxW="container.lg" mx="auto">
        <VStack spacing={8}>
          <Heading as="h2" size="lg" textAlign="center">What Our Dashboard Offers</Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            <Card>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Heading size="md">Feature 1: Manage Calls and Agents</Heading>
                  <Text>
                    Effortlessly track and manage all your voice agent calls. View detailed call history, recordings, and analytics to monitor performance.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Heading size="md">Feature 2: Chatbot and Voice Agent Settings</Heading>
                  <Text>
                    Configure and customize your chatbot and voice agents. Set up knowledge bases, define behaviors, and integrate with other services.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Heading size="md">Feature 3: Analytics and Reporting</Heading>
                  <Text>
                    Gain valuable insights into your agent performance and user interactions with comprehensive analytics and customizable reports.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <VStack spacing={4} align="start">
                  <Heading size="md">Feature 4: User and Account Management</Heading>
                  <Text>
                    Administer users, manage accounts, and control access levels with a robust user management system.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg={useColorModeValue('white', 'gray.800')} color={useColorModeValue('gray.800', 'white')}>
          <ModalHeader borderBottomWidth="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>Club Service Request Form</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} as="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
              {/* Club Details */}
              <Box width="100%" p={6} borderWidth="1px" borderRadius="lg" boxShadow="md" bg={useColorModeValue('gray.50', 'gray.700')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
                <Heading size="md" textAlign="center" mb={6}>users Details</Heading>
                <VStack spacing={4}>
                  <FormControl id="clubName" isRequired>
                    <FormLabel>users Name</FormLabel>
                    <Input type="text" value={formData.clubName} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="clubEmail" isRequired>
                    <FormLabel>users Email</FormLabel>
                    <Input type="email" value={formData.clubEmail} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="clubPhone">
                    <FormLabel>users Phone Number</FormLabel>
                    <Input type="tel" value={formData.clubPhone} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="clubWebsite">
                    <FormLabel>users Website (if any)</FormLabel>
                    <Input type="url" value={formData.clubWebsite} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="clubCity" isRequired>
                    <FormLabel>City</FormLabel>
                    <Input type="text" value={formData.clubCity} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="clubCountry" isRequired>
                    <FormLabel>Country</FormLabel>
                    <Input type="text" value={formData.clubCountry} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                </VStack>
              </Box>

              <Divider /> {/* Add a separator */}

              {/* Contact Person */}
              <Box width="100%" p={6} borderWidth="1px" borderRadius="lg" boxShadow="md" bg={useColorModeValue('gray.50', 'gray.700')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
                <Heading size="md" textAlign="center" mb={6}>Contact Person</Heading>
                <VStack spacing={4}>
                  <FormControl id="contactName" isRequired>
                    <FormLabel>Contact Person Name</FormLabel>
                    <Input type="text" value={formData.contactName} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="contactRole">
                    <FormLabel>Contact Person Role</FormLabel>
                    <Select placeholder="Select role" value={formData.contactRole} onChange={handleSelectChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} color={useColorModeValue('gray.800', 'white')} iconColor={useColorModeValue('gray.800', 'white')}>
                      <option value="Manager">Manager</option>
                      <option value="Coach">Coach</option>
                      <option value="Admin">Admin</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Other">Other</option>
                    </Select>
                  </FormControl>
                  <FormControl id="contactEmail" isRequired>
                    <FormLabel>Contact Person Email</FormLabel>
                    <Input type="email" value={formData.contactEmail} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                  <FormControl id="contactPhone">
                    <FormLabel>Contact Person Phone Number</FormLabel>
                    <Input type="tel" value={formData.contactPhone} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                  </FormControl>
                </VStack>
              </Box>

               <Divider /> {/* Add a separator */}

              {/* Services Requested */}
              <Box width="100%" p={6} borderWidth="1px" borderRadius="lg" boxShadow="md" bg={useColorModeValue('gray.50', 'gray.700')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
                <Heading size="md" textAlign="center" mb={6}>Services Requested</Heading>
                <VStack spacing={4}>
                 <FormControl id="services">
                   <FormLabel>Select Services</FormLabel>
                    {/* Styled Select for multiple selection */}
                    <Select placeholder="Select services" value={formData.timeline} onChange={handleSelectChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} color={useColorModeValue('gray.800', 'white')} iconColor={useColorModeValue('gray.800', 'white')}>
                    <option value="ticketSystem">Ticket Booking System</option>
                     <option value="sponsorship">Sponsorship Management</option>
                     <option value="support">Customer Support</option>
                     <option value="fanEngagement">Fan Engagement Tools</option>
                     <option value="whiteLabel">White Label Solution</option>
                     <option value="ecommerce">E-Commerce Channel</option>
                     <option value="other">Other (Specify Below)</option>
                   
                  </Select>
                  
                 </FormControl>
                <FormControl id="otherService">
                  <FormLabel>Other (please specify):</FormLabel>
                  <Input type="text" placeholder="Specify other services" value={formData.otherService} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                </FormControl>
                </VStack>
              </Box>

               <Divider /> {/* Add a separator */}

              {/* Additional Details */}
              <Box width="100%" p={6} borderWidth="1px" borderRadius="lg" boxShadow="md" bg={useColorModeValue('gray.50', 'gray.700')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
                <Heading size="md" textAlign="center" mb={6}>Additional Details</Heading>
                 <VStack spacing={4}>
                <FormControl id="clubNeeds">
                  <FormLabel>Describe your user's specific needs or goals</FormLabel>
                  <Textarea rows={3} value={formData.clubNeeds} onChange={handleInputChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }} color={useColorModeValue('gray.800', 'white')}/>
                </FormControl>
                <FormControl id="timeline">
                  <FormLabel>Preferred Collaboration Timeline</FormLabel>
                  <Select placeholder="Select timeline" value={formData.timeline} onChange={handleSelectChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} color={useColorModeValue('gray.800', 'white')} iconColor={useColorModeValue('gray.800', 'white')}>
                    <option value="1-3 months">1-3 months</option>
                    <option value="3-6 months">3-6 months</option>
                    <option value="6+ months">6+ months</option>
                  </Select>
                </FormControl>
                <FormControl id="budget">
                  <FormLabel>Estimated Budget</FormLabel>
                  <Select placeholder="Select your budget range" value={formData.budget} onChange={handleSelectChange} bg={useColorModeValue('white', 'gray.800')} borderColor={useColorModeValue('gray.300', 'gray.600')} color={useColorModeValue('gray.800', 'white')} iconColor={useColorModeValue('gray.800', 'white')}>
                    <option value="200-500">$200 - $500</option>
                    <option value="500-700">$500 - $700</option>
                    <option value="700-1000">$700 - $1000</option>
                    <option value="1000+">$1000+</option>
                  </Select>
                </FormControl>
                 </VStack>
              </Box>

               <Divider /> {/* Add a separator */}

              {/* Consent */}
              <Box width="100%" p={6} borderWidth="1px" borderRadius="lg" boxShadow="md" bg={useColorModeValue('gray.50', 'gray.700')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
                <Heading size="md" textAlign="center" mb={6}>Consent & Confirmation</Heading>
                <VStack spacing={4}>
                  <FormControl id="authConfirm">
                    <Checkbox isRequired isChecked={formData.authConfirm} onChange={handleInputChange} colorScheme="teal">I confirm that I am authorized to submit this request on behalf of the club.</Checkbox>
                  </FormControl>
                  <FormControl id="termsAgree">
                    <Checkbox isRequired isChecked={formData.termsAgree} onChange={handleInputChange} colorScheme="teal">I agree to the Terms & Privacy Policy.</Checkbox>
                  </FormControl>
                </VStack>
              </Box>

              <Button colorScheme="teal" size="lg" type="submit" mt={4}>Submit Form</Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Home; 